import { FloppyDisk, Clock, NotePencil, Lock, LockOpen, ShieldCheck, Eye, Trash } from 'phosphor-react';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { monthLabel, monthKey, buildMonthOptions } from '../utils/dates';
import { clearMonthEntry, replaceHoursState, selectMonth, setMonthHours, setMonthNotes } from '../redux/slices/hoursSlice';
import { hrDashboardService } from '../services/hrDashboardService';
import { useToast } from '../components/Toast';

const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedMonth, months } = useAppSelector((state) => state.hours);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    let isActive = true;

    const loadState = async () => {
      try {
        const remoteState = await hrDashboardService.getHoursState();
        if (isActive) {
          if (Object.keys(remoteState.months).length > 0 || remoteState.selectedMonth) {
            dispatch(replaceHoursState(remoteState));
          }
        }
      } catch (error) {
        const status = typeof error === 'object' && error !== null && 'response' in error
          ? Number((error as { response?: { status?: number } }).response?.status)
          : null;

        if (isActive && status !== 404) {
          showToast(error instanceof Error ? error.message : 'Failed to load saved hours', 'error');
        }
      }
    };

    loadState();

    return () => {
      isActive = false;
    };
  }, [dispatch, user]);

  if (!user) {
    return null;
  }

  const canFillHours = user.hrDashboardAdmin || user.hrDashboardFiller;
  const monthOptions = buildMonthOptions(12);
  const activeMonth = selectedMonth || monthKey(new Date());
  const monthState = months[activeMonth] ?? { locked: false, entries: {} };
  const entry = monthState.entries[String(user.id)] ?? {
    month: activeMonth,
    userId: user.id,
    userName: user.name,
    hours: 0,
    notes: '',
    updatedAt: new Date().toISOString(),
  };
  const totalHours = Number(entry.hours) || 0;
  const locked = monthState.locked;
  const canEdit = canFillHours && !locked;
  const canSaveState = canFillHours;

  const handleSave = async () => {
    if (!canSaveState) {
      return;
    }

    setIsSaving(true);

    try {
      await hrDashboardService.saveHoursState({
        selectedMonth,
        months,
      });
      showToast('Hours saved');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save hours', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    if (!canSaveState) {
      return;
    }

    setIsSaving(true);

    try {
      const nextMonths = {
        ...months,
        [activeMonth]: {
          ...monthState,
          entries: { ...monthState.entries },
        },
      };
      delete nextMonths[activeMonth].entries[String(user.id)];

      dispatch(clearMonthEntry({ month: activeMonth, userId: user.id }));
      await hrDashboardService.saveHoursState({
        selectedMonth,
        months: nextMonths,
      });
      showToast('Entry cleared');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to clear entry', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative isolate space-y-6">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.12] to-white/[0.04] p-6 text-white shadow-glow backdrop-blur-2xl sm:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.25em] text-brand-200/90">
              <Clock size={14} weight="fill" /> Your hours
            </div>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">{monthLabel(activeMonth)}</h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-white/70 sm:text-base">
              {canEdit
                ? 'Capture your monthly total, add a short note, and keep the process simple.'
                : 'This month is read-only for your account.'}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <label className="text-xs uppercase tracking-[0.2em] text-white/45">Month</label>
            <select
              value={activeMonth}
              onChange={(event) => dispatch(selectMonth(event.target.value))}
              className="min-w-44 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
            >
              {monthOptions.map((month) => (
                <option key={month} value={month} className="bg-slate-900">
                  {monthLabel(month)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_340px]">
        <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl sm:p-8">
          <div className="flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-brand-200/90">Monthly entry</div>
              <h2 className="mt-2 text-2xl font-semibold text-white">Submit your hours</h2>
            </div>
            <div
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${
                canEdit
                  ? 'border-brand-400/30 bg-brand-500/10 text-brand-100'
                  : 'border-amber-400/30 bg-amber-500/10 text-amber-100'
              }`}
            >
              {canEdit ? <LockOpen size={16} weight="fill" /> : <Lock size={16} weight="fill" />}
              {canEdit ? 'Editable' : 'Read only'}
            </div>
          </div>

          {/* Big hours display */}
          <div className="mt-6 flex flex-col items-center justify-center rounded-[1.5rem] border border-white/10 bg-gradient-to-b from-brand-500/10 to-transparent px-6 py-8 text-center">
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">Hours this month</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-6xl font-bold tracking-tight text-white">{totalHours.toFixed(2)}</span>
              <span className="text-lg font-medium text-white/50">hrs</span>
            </div>
          </div>

          <div className="mt-6 space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-white/85">Hours for the month</label>
              <div className="relative">
                <Clock size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type="number"
                  value={entry.hours}
                  disabled={!canEdit}
                  onChange={(event) =>
                    dispatch(
                      setMonthHours({
                        month: activeMonth,
                        userId: user.id,
                        userName: user.name,
                        hours: Number(event.target.value),
                      })
                    )
                  }
                  className="w-full rounded-[1.25rem] border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20 disabled:cursor-not-allowed disabled:opacity-60"
                  placeholder="0.00"
                />
              </div>
              <p className="mt-2 text-xs text-white/45">Round to quarter hours if needed.</p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-white/85">Notes</label>
              <div className="relative">
                <NotePencil size={18} className="pointer-events-none absolute left-4 top-4 text-white/35" />
                <textarea
                  value={entry.notes}
                  disabled={!canEdit}
                  onChange={(event) =>
                    dispatch(
                      setMonthNotes({
                        month: activeMonth,
                        userId: user.id,
                        userName: user.name,
                        notes: event.target.value,
                      })
                    )
                  }
                  rows={5}
                  className="w-full rounded-[1.25rem] border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20 disabled:cursor-not-allowed disabled:opacity-60"
                  placeholder="Optional context for this month"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-white/50">Changes are saved to the server for your account.</div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={!canSaveState || isSaving}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Trash size={16} /> Clear entry
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSaveState || isSaving}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FloppyDisk size={18} /> {isSaving ? 'Saving...' : 'Save hours'}
                </button>
              </div>
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400/30 to-brand-600/20 text-lg font-semibold text-brand-100 ring-1 ring-brand-400/20">
                {user.name?.charAt(0)?.toUpperCase() ?? '?'}
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold text-white">{user.name}</h2>
                <p className="truncate text-xs text-white/50">{user.email}</p>
              </div>
            </div>

            <div className="mt-6 space-y-3 text-sm text-white/75">
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                <span className="text-xs uppercase tracking-[0.22em] text-white/45">Month total</span>
                <span className="text-base font-semibold text-brand-200">{totalHours.toFixed(2)} h</span>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                <div className="flex items-center gap-2">
                  {canEdit ? (
                    <ShieldCheck size={18} weight="fill" className="text-brand-300" />
                  ) : (
                    <Eye size={18} weight="fill" className="text-amber-300" />
                  )}
                  <span className="font-medium text-white">{canEdit ? 'Editing allowed' : 'Read only'}</span>
                </div>
                <div className="mt-1 text-xs text-white/55">
                  {canEdit ? 'You can update this month now.' : 'This month is locked or read only.'}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">Quick tip</div>
            <p className="mt-2 text-sm leading-6 text-white/70">
              Submit your total once per month. You can revisit and update it any time before the month is locked.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default DashboardPage;